import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Column, Dialog, DialogActions, DialogContent, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { useEffect, useLayoutEffect, useRef } from 'react';
import { FormProvider, useForm, type Resolver } from 'react-hook-form';
import { FormErrorBanner, useFormSubmit } from '~/components/shared/forms';
import {
  OFFICE_MISSING_INFO_COPY as COPY,
  useSaveOfficeMissingInfo,
  type IOfficeMissingInfoFormValues,
  type IOfficeMissingInfoSource,
  type TMissingOfficeInfoField,
} from '~/domain/OfficeMissingInfo';
import { Illustration } from '@tempmee/illustrations/react';
import { fireShiftRequestMissingInfoSubmitted } from '~/posthogs/events';
import {
  buildOfficeMissingInfoSchema,
  OFFICE_MISSING_INFO_DEFAULT_VALUES,
} from './officeMissingInfoSchema';
import { OfficeMissingInfoFields } from './OfficeMissingInfoFields';
import { type IOfficeMissingInfoCatalog } from './useOfficeMissingInfoCatalog';

// Figma frame width (node 7020:74310); the DS `medium` panel is 620. Same cap as
// ReferralNotEligibleDialog. The height cap keeps the required Save on screen: the DS
// panel has no max-height, and its overlay pads the viewport by 16px on each side.
const PANEL_STYLE = { maxWidth: 358, maxHeight: 'calc(100vh - 32px)' } as const;
const CONTENT_STYLE = { overflowY: 'auto', minHeight: 0 } as const;
const ILLUSTRATION_SIZE = 104;

/** The section each field renders in, in display order; a failed Save scrolls to the first invalid one. */
const SECTION_BY_FIELD: ReadonlyArray<[keyof IOfficeMissingInfoFormValues, string]> = [
  ['timePerPatient', 'missing-info-time-per-patient-section'],
  ['isAssistedHygiene', 'missing-info-assisted-hygiene-section'],
  ['parkingType', 'missing-info-parking-section'],
  ['isParkingFree', 'missing-info-parking-section'],
  ['parkingCost', 'missing-info-parking-section'],
  ['parkingCostType', 'missing-info-parking-section'],
  ['softwares', 'missing-info-softwares-section'],
  ['insurances', 'missing-info-insurances-section'],
  ['operatories', 'missing-info-operatories-section'],
];

// jsdom has no scrollIntoView; the optional call keeps the component testable.
const scrollTo = (element: Element | null | undefined): void =>
  element?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });

/** `Dialog` labels itself from a string `DialogTitle`; the heading lives in the content instead. */
const TITLE_ID = 'office-missing-info-title';
const POPUP_DATA_CY = 'missing-info-popup';

const noop = (): void => {};

export interface IOfficeMissingInfoModalProps {
  /** The fields the office still lacks, in display order. */
  missingFields: TMissingOfficeInfoField[];
  /** Reduced profession code (RDH, DA, DN, FD). */
  professionCode: string;
  /** What the office already has saved; unasked parking parts are merged from it. */
  source: IOfficeMissingInfoSource;
  /** The office the save is scoped to. */
  officeId: string;
  /**
   * Whether `officeId` is the store's selected office (default). False for the child a parent picked in the
   * shift form (GOTU-14829): the save is scoped to the child and the parent stays selected.
   */
  isSelectedOffice?: boolean;
  /** The software and insurance names the pickers offer. */
  catalog: IOfficeMissingInfoCatalog;
  /** Called after a successful save with the values that were sent. */
  onSaved: (values: IOfficeMissingInfoFormValues) => void;
}

const OfficeMissingInfoForm = ({
  missingFields,
  professionCode,
  source,
  officeId,
  isSelectedOffice = true,
  catalog,
  onSaved,
}: IOfficeMissingInfoModalProps) => {
  const { mutateAsync, isPending } = useSaveOfficeMissingInfo(officeId, { isSelectedOffice });

  // The dialog content is its own scroll box, so useFormSubmit's page-level banner scroll does not
  // apply here. A Save with a field left empty scrolls that field's section into view once the banner
  // has rendered above it, so the office sees what is missing instead of nothing happening
  // (GOTU-15827); a save the server rejected scrolls to the banner that explains it.
  const scrollFrameRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (scrollFrameRef.current !== null) cancelAnimationFrame(scrollFrameRef.current);
    },
    [],
  );

  const { submit, bannerVisible, bannerRef, serverMessage } =
    useFormSubmit<IOfficeMissingInfoFormValues>({
      onValid: async (values) => {
        try {
          await mutateAsync({ values, missingFields, professionCode, source });
        } catch {
          throw new Error(COPY.saveError);
        }
        // The submitted event is the conversion signal: one per submission that actually saved. Firing it
        // on the click counted a failed save and its retry as two submissions.
        fireShiftRequestMissingInfoSubmitted({ professionType: professionCode, missingFields });
        onSaved(values);
      },
      onInvalid: (errors) => {
        const firstInvalid = SECTION_BY_FIELD.find(([field]) => errors[field]);
        if (!firstInvalid) return;
        scrollFrameRef.current = requestAnimationFrame(() => {
          scrollFrameRef.current = null;
          scrollTo(
            document.querySelector(`[data-cy="${POPUP_DATA_CY}"] [data-cy="${firstInvalid[1]}"]`),
          );
        });
      },
      scrollOnInvalid: false,
    });

  useEffect(() => {
    if (bannerVisible && serverMessage) scrollTo(bannerRef.current);
  }, [bannerVisible, serverMessage, bannerRef]);

  return (
    <Dialog
      open
      onClose={noop}
      size="medium"
      closeOnOverlayClick={false}
      closeOnEscape={false}
      style={PANEL_STYLE}
      aria-labelledby={TITLE_ID}
      data-cy={POPUP_DATA_CY}
    >
      {/* No DialogTitle: the Figma title bar and close button are hidden — this modal can't be
          dismissed. DialogContent brings `px-4 pb-5`; the top 16 comes from `pt`. */}
      <DialogContent style={CONTENT_STYLE}>
        <Column gap={6} pt={4}>
          <Column gap={1} align="start">
            {/* Decorative: the heading carries the meaning, so no alt text. */}
            <Illustration name="building" size={ILLUSTRATION_SIZE} />
            <Column>
              <Typography id={TITLE_ID} variant="subtitle1" bold color={colors.text.primary}>
                {COPY.title}
              </Typography>
              <Typography variant="body1" color={colors.text.secondary}>
                {COPY.description}
              </Typography>
            </Column>
          </Column>
          <FormErrorBanner ref={bannerRef} visible={bannerVisible} message={serverMessage} />
          <OfficeMissingInfoFields
            missingFields={missingFields}
            professionCode={professionCode}
            catalog={catalog}
          />
        </Column>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          color="primary"
          size="small"
          data-cy="missing-info-save"
          onClick={submit}
          isLoading={isPending}
        >
          {COPY.save}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * The required "missing office details" modal (GOTU-12579, Figma 7020:74310). It has no close,
 * back or skip control, and Escape and the backdrop do nothing: the office saves or stays.
 */
export const OfficeMissingInfoModal = (props: IOfficeMissingInfoModalProps) => {
  // The asked fields can shrink while the modal is open (a partial save, then a re-read), so the
  // resolver reads the latest list instead of the one the form was created with.
  // Refreshed after commit, never during render; Save is a user event, so it always sees the
  // latest list.
  const missingFieldsRef = useRef(props.missingFields);
  useLayoutEffect(() => {
    missingFieldsRef.current = props.missingFields;
  });

  const resolver: Resolver<IOfficeMissingInfoFormValues> = (values, context, options) =>
    zodResolver(buildOfficeMissingInfoSchema(missingFieldsRef.current))(values, context, options);

  const methods = useForm<IOfficeMissingInfoFormValues>({
    defaultValues: OFFICE_MISSING_INFO_DEFAULT_VALUES,
    resolver,
  });

  return (
    <FormProvider {...methods}>
      <OfficeMissingInfoForm {...props} />
    </FormProvider>
  );
};
